import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import { getAllProducts, clearProductMatch } from "../../API/Backend";
import { showToast } from "../../Components/Toast";
import MatchModal from "../../Components/MatchModal";

const SOURCE_LABEL = { amazon: "Amazon", carrefour: "Carrefour" };
const OPPOSITE = { amazon: "carrefour", carrefour: "amazon" };

export default function ProductListing({ source }) {
  const label = SOURCE_LABEL[source];
  const oppositeLabel = SOURCE_LABEL[OPPOSITE[source]];

  const [products, setProducts] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [matchProduct, setMatchProduct] = useState(null);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    sortField: "title",
    sortOrder: 1,
    filters: {
      global: { value: "", matchMode: "contains" },
      title: { value: null, matchMode: "contains" },
    },
  });

  useEffect(() => {
    fetchProducts(lazyParams);
  }, [lazyParams]);

  const fetchProducts = (params) => {
    setLoading(true);
    setProducts([]);
    getAllProducts({
      ...params,
      type: source,
      filters: JSON.stringify(params.filters),
    })
      .then(({ data }) => {
        if (data?.data) {
          setProducts(data.data.products || []);
          setCount(data.data.count || 0);
        } else {
          showToast("error", "Request Failed", data?.message);
        }
      })
      .catch((err) => showToast("error", "Request Failed", err.message))
      .finally(() => setLoading(false));
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setLazyParams((prev) => ({
      ...prev,
      first: 0,
      filters: { ...prev.filters, global: { value, matchMode: "contains" } },
    }));
  };

  const onPage = (e) => setLazyParams((prev) => ({ ...prev, ...e }));
  const onSort = (e) => setLazyParams((prev) => ({ ...prev, sortField: e.sortField, sortOrder: e.sortOrder }));
  const onFilter = (e) => setLazyParams((prev) => ({ ...prev, filters: e.filters, first: 0 }));

  const handleMatchClose = (saved) => {
    setMatchProduct(null);
    if (saved) fetchProducts(lazyParams);
  };

  const handleClearMatch = (product) => {
    clearProductMatch(product._id)
      .then(({ data }) => {
        if (data?.success) {
          showToast("success", "Match Cleared", "Match has been removed.");
          fetchProducts(lazyParams);
        } else {
          showToast("error", "Failed", data?.message);
        }
      })
      .catch((err) => showToast("error", "Failed", err.message));
  };

  const header = (
    <div className="d-flex align-items-center gap-3">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={lazyParams.filters.global.value || ""}
          onChange={onGlobalFilterChange}
          placeholder="Search by name…"
        />
      </span>
      <span className="ms-auto text-muted" style={{ fontSize: 13 }}>
        {count} product{count !== 1 ? "s" : ""}
      </span>
    </div>
  );

  return (
    <Container fluid>
      <Row className="align-items-center mb-3">
        <Col>
          <h5 className="page-title">{label} Products</h5>
          <Card>
            <CardBody>
              <DataTable
                value={products}
                lazy
                loading={loading}
                first={lazyParams.first}
                rows={lazyParams.rows}
                paginator
                onPage={onPage}
                onSort={onSort}
                sortOrder={lazyParams.sortOrder}
                sortField={lazyParams.sortField}
                filters={lazyParams.filters}
                onFilter={onFilter}
                totalRecords={count}
                emptyMessage="No products found."
                sortMode="single"
                rowsPerPageOptions={[10, 25, 50]}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                header={header}
              >
                {/* Image */}
                <Column
                  header="Image"
                  style={{ width: "6%" }}
                  body={(row) =>
                    loading ? (
                      <Skeleton height="40px" width="70px" />
                    ) : (
                      <img
                        src={row.images?.[0]}
                        alt=""
                        height={40}
                        width={70}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )
                  }
                />

                {/* Title */}
                <Column
                  field="title"
                  header="Product Name"
                  sortable
                  style={{ width: "30%" }}
                  body={(row) =>
                    loading ? (
                      <Skeleton height="12px" width="80%" />
                    ) : (
                      <div>
                        <div style={{ fontWeight: 500 }}>{row.title}</div>
                        {row.link && (
                          <a href={row.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#888" }}>
                            View on {label} ↗
                          </a>
                        )}
                      </div>
                    )
                  }
                />

                {/* Category */}
                <Column
                  header="Category"
                  style={{ width: "12%" }}
                  body={(row) =>
                    loading ? <Skeleton height="12px" width="70%" /> : (
                      <span style={{ fontSize: 13 }}>
                        {row.categoryId?.title || row.category || "—"}
                      </span>
                    )
                  }
                />

                {/* Price */}
                <Column
                  header="Price (AED)"
                  sortable
                  sortField={source === "amazon" ? "amazonPrice" : "carrefourPrice"}
                  style={{ width: "10%" }}
                  body={(row) =>
                    loading ? <Skeleton height="20px" width="60px" /> : (
                      <strong>AED {row.price ?? "—"}</strong>
                    )
                  }
                />

                {/* Price per unit — Carrefour only */}
                {source === "carrefour" && (
                  <Column
                    header="Per Unit"
                    style={{ width: "10%" }}
                    body={(row) =>
                      loading ? <Skeleton height="12px" width="70px" /> : (
                        <span className="text-muted" style={{ fontSize: 13 }}>{row.pricePerUnit || "—"}</span>
                      )
                    }
                  />
                )}

                {/* Match status */}
                <Column
                  header="Match Status"
                  style={{ width: "12%" }}
                  body={(row) =>
                    loading ? <Skeleton height="20px" width="80px" /> : (
                      row.matchedProductId ? (
                        <span className="badge bg-success">Matched</span>
                      ) : (
                        <span className="badge bg-secondary">Unmatched</span>
                      )
                    )
                  }
                />

                {/* Matched product */}
                <Column
                  header={`Matched ${oppositeLabel} Product`}
                  style={{ width: "20%" }}
                  body={(row) =>
                    loading ? <Skeleton height="12px" width="80%" /> : (
                      row.matchedProductId ? (
                        <div style={{ fontSize: 12 }}>
                          <div style={{ fontWeight: 500 }}>{row.bestMatch}</div>
                          {row[`${OPPOSITE[source]}Price`] && (
                            <span className="text-muted">AED {row[`${OPPOSITE[source]}Price`]}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                      )
                    )
                  }
                />

                {/* Actions */}
                <Column
                  header="Actions"
                  style={{ width: "14%" }}
                  body={(row) =>
                    loading ? <Skeleton height="30px" width="100px" /> : (
                      <div className="d-flex flex-column gap-1">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setMatchProduct(row)}
                          style={{ fontSize: 12 }}
                        >
                          <i className="ri-links-line me-1" />
                          {row.matchedProductId ? `Re-match ${oppositeLabel}` : `Match ${oppositeLabel}`}
                        </button>
                        {row.matchedProductId && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleClearMatch(row)}
                            style={{ fontSize: 12 }}
                          >
                            <i className="ri-link-unlink-m me-1" />Clear Match
                          </button>
                        )}
                      </div>
                    )
                  }
                />
              </DataTable>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {matchProduct && (
        <MatchModal
          visible={!!matchProduct}
          product={matchProduct}
          onClose={handleMatchClose}
        />
      )}
    </Container>
  );
}
